import { Button, buttonVariants } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { Link, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useDoctor } from "../../lib/hooks/doctors/use-doctor";
import { CircleUserRound } from "lucide-react";
import { useAppointmentData } from "../../contexts/appointment-data-context";
import { useToast } from "../../components/ui/use-toast";
import { useDoctorSlots } from "../../lib/hooks/doctors/use-doctor-slots";
import { useEffect, useMemo, useState } from "react";
import { Textarea } from "../../components/ui/textarea";
import { useSymptoms } from "../../lib/hooks/doctors/use-symptoms";
import { useAuth } from "../../contexts/auth-context";
import { usePatientsByMobile } from "../../lib/hooks/doctors/use-patients-by-mobile";
import { IPatient } from "../../types";
import Nav from "./nav";
import PopaddPatient from "./pop-add-patient";
import PopverifyPatient from "./pop-verify-patient";
import LargeConfirmAppointment from "./large-confirm-appointment";
import PopAddHealthRecords from "./pop-add-health-records";

const MAX_SLOTS = 6;
const MAX_VISIBLE_CLINICS = 1;
const today = format(new Date(), "dd-MM-yyyy");

function DoctorDetails1() {
  const [searchParams] = useSearchParams();
  const initialBackUrl = searchParams.get("backUrl") || "/doctors-list";
  const [backUrl] = useState(initialBackUrl);
  const { mobile, fetched } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const professionalId = params.professionalId?.split("-").pop(); // ✅ extracts correct ID even from slug
  const urlStep = params.step;
  const [step, setStep] = useState(Number(urlStep) || 1);
  const [showModal, setShowModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showAddHealthRecords, setShowAddHealthRecords] = useState(false);
  const [showVerifyPatient, setShowVerifyPatient] = useState(false);
  const [showPatientSelection, setShowPatientSelection] = useState(false);
  const [isDirectNavigation, setIsDirectNavigation] = useState(true);

  const { data, setData } = useAppointmentData();
  const { doctor, isLoading } = useDoctor(professionalId);
  useEffect(() => {
    const directlyAccessedDoctor = !searchParams.get("backUrl") && window.history.length <= 1;
    //console.log("Directly accessed?", directlyAccessedDoctor);
    localStorage.setItem("directAccessedDoctor", String(directlyAccessedDoctor));
    setIsDirectNavigation(directlyAccessedDoctor);
  }, []);


  const seoPath = useMemo(() => {
    if (!doctor) return "";

    const name = doctor.fullName?.toLowerCase().replace(/\s+/g, "-") || "unknown";
    const specialty =
      doctor.specialities
        ?.map((s) => s.toLowerCase().replace(/\s+/g, "-"))
        .join("-") || "general";

    // Get location from first branch
    const branch = doctor.branchDetails?.[0] || {};
    const state = branch.state?.trim() ? branch.state.toLowerCase().replace(/\s+/g, "-") : null;
    const district = branch.district?.trim() ? branch.district.toLowerCase().replace(/\s+/g, "-") : null;
    const city = branch.subDistrictOrCity?.trim() ? branch.subDistrictOrCity.toLowerCase().replace(/\s+/g, "-") : null;

    // Dynamically build location parts based on availability
    const locationParts = [];
    if (state) locationParts.push(state);
    if (district) locationParts.push(district);
    if (city) locationParts.push(city);

    // Build final URL
    const url = `/doctors/${name}-${specialty}${locationParts.length ? `-${locationParts.join("-")}` : ""}-${professionalId}`;

    //console.log("Generated SEO URL:", url);

    return url;
  }, [doctor, professionalId]);

  useEffect(() => {
    if (!isLoading && seoPath && window.location.pathname === `/doctors/${professionalId}`) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("backUrl", backUrl);
      navigate(`${seoPath}?${updatedSearchParams.toString()}`, { replace: true });
    }
  }, [isLoading, seoPath, professionalId, navigate]);


  const [selectedBranch, setSelectedBranch] = useState<number | undefined>();

  const {
    slots = {},
    isLoading: isSlotsLoading,
    isFetched: isSlotsFetched,
  } = useDoctorSlots(professionalId, selectedBranch);

  const [selectedDate, setSelectedDate] = useState(today);
  const selectedDateSlots = useMemo(
    () => slots[selectedDate] ?? [],
    [slots, selectedDate],
  );

  const [paginatedSlots, setPaginatedSlots] = useState<
    typeof selectedDateSlots
  >([]);

  const { patients } = usePatientsByMobile({});
  const { symptoms = [], isLoading: isSymptomsLoading } = useSymptoms({
    enabled: !!(doctor?.specialityIds ?? []).length,
    specialityIds: doctor?.specialityIds || [],
  })

  function handleSelectPatient(patient: IPatient) {
    setData({
      ...data,
      patient: {
        id: patient.patientId,
        name: patient.name,
      },
      branchDetails: data?.branchDetails || [],
    });
    // console.log("Selected patient:", patient);
    // console.log("Data after selection:", data);
  }

  function handleTypeSymptoms(symptoms: string) {
    const newSymptoms = symptoms.split(",").map((s) => s.trim());
    setData({
      ...data,
      symptoms: newSymptoms,
      branchDetails: data?.branchDetails || [],
    });
  }

  function handleToggleSelectSymptom(symptom: string) {
    setData({
      ...data,
      symptoms: data?.symptoms?.includes(symptom)
        ? data?.symptoms?.filter((s) => s !== symptom)
        : [...(data?.symptoms || []), symptom],
      branchDetails: data?.branchDetails || [],
    });
  }

  function handleNextStep() {
    if (step === 1 && !data?.slotTiming) {
      toast({
        title: "Please select a slot to continue",
        variant: "destructive",
      });
    } else if (step === 2 && mobile && !data?.patient) {
      toast({
        title: "Please select a patient to continue",
        variant: "destructive",
      });
    } else if (step === 2 && !mobile && showPatientSelection && !data?.patient) {
      toast({
        title: "Please select a patient to continue",
        variant: "destructive",
      });
    } else if (!mobile && step === 2 && !showPatientSelection) {
      setShowVerifyPatient(true);
    } else {
      const nextStep = mobile || (!mobile && !showPatientSelection) ? step + 1 : 4;
      setStep(nextStep);
      navigate(
        `?step=${nextStep}&backUrl=${encodeURIComponent(backUrl)}`,
      );
    }
  }

  const handlePrevStep = () => {
    if (step === 2 && !mobile && showPatientSelection) {
      setShowPatientSelection(false);
      setShowVerifyPatient(false);
    } else if (step === 2 && !mobile && showVerifyPatient) {
      setShowVerifyPatient(false);
    } else {
      setStep((prev) => Math.max(prev - 1, 1));
      navigate(`?step=${Math.max(step - 1, 1)}&backUrl=${encodeURIComponent(backUrl)}`);
    }
  };

  function handleContinue() {
    if (mobile && patients?.length && !data?.patient) {
      return toast({
        title: "Please select a patient to continue",
        variant: "destructive",
      });
    }

    if (mobile && !patients?.length && professionalId) {
      return navigate(
        `/add-patient?redirectUrl=/doctors/${professionalId}/appointment/confirm&backUrl=/doctors/${professionalId}/appointment`,
      );
    }

    if (mobile) {
      setStep(4);
      navigate(
        `/doctors/${doctor?.professionalId}?step=4&backUrl=${encodeURIComponent(backUrl)}`,
      );
    } else {
      const verifyUrl = `/verify-patient?redirect=/doctors/${doctor?.professionalId}/appointment?step=2&showPatientSelection=true&backUrl=${encodeURIComponent(backUrl)}`;
      navigate(verifyUrl);
    }
  }

  // useEffect(() => {
  //   if (
  //     !isLoading &&
  //     (!data?.slotDate || !data.slotTiming || !data.dayOfWeek)
  //   ) {
  //     navigate(`/doctors/${doctor?.professionalId}`);
  //   }
  // }, [data, doctor?.professionalId, navigate, isLoading]);

  useEffect(() => {
    if (patients?.length === 0 && !showSignupModal && mobile) {
      setShowModal(true);
    }
  }, [patients, showSignupModal, mobile]);

  function handleSelectSlot(slot: { from: string; to: string }) {
    const [day, month, year] = selectedDate.split("-").map(Number);
    const jsDate = new Date(year, month - 1, day);

    setData({
      ...data,
      slotTiming: `${slot.from} - ${slot.to}`,
      dayOfWeek: format(jsDate, "EEEE"),
      slotDate: jsDate,
      branchDetails: data?.branchDetails || [],
    });
  }

  useEffect(() => {
    if (isSlotsFetched) {
      setSelectedDate(Object.keys(slots)[0] || today);
    }
  }, [isSlotsFetched, slots]);

  useEffect(() => {
    if (selectedDateSlots.length) {
      setPaginatedSlots(selectedDateSlots.slice(0, MAX_SLOTS));
    }
  }, [selectedDateSlots]);

  const storedPatient = localStorage.getItem("selectedPatient");
  const [selectedPatient, setSelectedPatient] = useState(storedPatient);

  const [showAllClinics, setShowAllClinics] = useState(false);
  const branchDetails = doctor?.branchDetails || [];
  const visibleBranches = showAllClinics
    ? branchDetails
    : branchDetails.slice(0, MAX_VISIBLE_CLINICS);

  useEffect(() => {
    if (doctor?.branchDetails?.length) {
      const defaultBranch = doctor.branchDetails[0];
      setSelectedBranch(defaultBranch.branchId);
      setData({
        ...data,
        branchDetails: [defaultBranch],
      });
    }
  }, [doctor, setData]);

  const handleBranchSelect = (branchId: number) => {
    const selectedBranch1 = doctor?.branchDetails?.find(
      (branch) => branch.branchId === branchId,
    );

    setSelectedBranch(branchId);

    if (selectedBranch1) {
      setData({
        ...data,
        branchDetails: [selectedBranch1],
      });
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const stepParam = Number(queryParams.get("step"));
    const showPatientSelectionParam = queryParams.get("showPatientSelection");
    if (stepParam) {
      setStep(stepParam);
    }
    if (showPatientSelectionParam === "true" && !mobile) {
      setShowPatientSelection(true);
    }
  }, [location.search]);

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50  bg-white px-4">
        <Nav
          selectedPatient={selectedPatient}
          setSelectedPatient={setSelectedPatient}
        />
      </div>

      <div className="sm:p-4 md:p-4 lg:min-h-screen lg:overflow-hidden lg:px-0 lg:py-0 bg-white lg:bg-[rgb(244,246,250)]">
        <div className="relative w-full mx-auto lg:px-0">
          <div className="relative group">

            {/* {(localStorage.getItem("directAccessedDoctor") !== "true" || step === 1) && ( */}
            {(!isDirectNavigation || searchParams.get("backUrl") && (Number(searchParams.get("step")) < 2)) && (
              <Link
                to={`/doctors`}
                className={buttonVariants({
                  className: "absolute left-4 top-0 mt-20",
                  variant: "ghost",
                  size: "icon",
                })}
                onClick={() => setData({ branchDetails: [] })}
              >
                <BackIcon />
                <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block items-center px-2 py-1 rounded text-xs text-white bg-gray-800 shadow-lg z-50">
                  Go Back
                </span>
              </Link>
            )}
          </div>

          <div className="lg:flex lg:px-0">
            <div
              className="lg:w-3/5 space-y-6 lg:overflow-y-auto lg:h-screen lg:p-10"
              style={{
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <div className="lg:flex">
                <div className="text-center lg:p-4 mt-14">
                  {doctor?.profilePhoto ? (
                    <img
                      src={`${doctor?.profilePhoto}`}
                      alt={doctor?.fullName}
                      className="mx-auto size-28 rounded-full object-contain"
                    />
                  ) : (
                    <div className="mx-auto size-28 rounded-full object-contain">
                      <CircleUserRound
                        className="size-full"
                        strokeWidth={1}
                      />
                    </div>
                  )}
                </div>

                <div className="lg:ml-18 mt-16">
                  <h2 className="text-lg font-bold">Dr {doctor?.fullName}</h2>
                  {doctor?.specialities && doctor.specialities.length > 0 && (
                    <p className="mt-2 text-sm ">
                      {doctor.specialities.join(", ")}
                    </p>
                  )}
                  <p className="mt-2 text-sm">
                    {doctor?.nameOfDegreeOrDiplomaObtained && doctor.nameOfDegreeOrDiplomaObtained.length > 0
                      ? doctor.nameOfDegreeOrDiplomaObtained.join(", ")
                      : ""}
                  </p>
                  <h4 className="mb-2 text-sm font-bold">{doctor?.clinicName}</h4>
                  {/* <p className="text-sm font-bold">
                    Fee: {doctor?.fee == null || Number(doctor?.fee) === 0.0 ? "NA" : `₹${doctor.fee}`}
                  </p> */}
                  <p className="text-sm">{doctor?.subDistrictOrCity === null ? "NA" : doctor?.subDistrictOrCity}</p>
                  <p className="text-sm">{doctor?.state === null ? "NA" : doctor?.state}</p>
                </div>
              </div>

              <div className="my-5 h-1.5 bg-border" />
              <div className="container">
                <div className="mb-6 space-y-4">
                  {visibleBranches.map((branch) => (
                    <div key={branch.branchId}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="clinicBranch"
                              checked={selectedBranch === branch.branchId}
                              onChange={() => handleBranchSelect(branch.branchId)}
                              className="mr-2"
                              style={{
                                accentColor: "hsl(var(--primary-2))",
                              }}
                            />
                            <h4 className="text-sm font-bold">
                              {branch.clinicName}
                            </h4>
                          </label>
                          {
                            // Only render if at least one field is non-null
                            (branch?.addressLine1 || branch?.addressLine2 || branch?.state || branch?.pincode) && (
                              <>
                                {
                                  // Render first <p> only if addressLine1 or addressLine2 is non-null
                                  (branch?.addressLine1 || branch?.addressLine2) && (
                                    <p className="text-sm ml-6">
                                      {[
                                        branch?.addressLine1,
                                        branch?.addressLine2
                                      ].filter(Boolean).join(", ")}
                                    </p>
                                  )
                                }
                                {
                                  // Render <p> only if at least one location field is non-null
                                  (branch?.state || branch?.district || branch?.subDistrictOrCity || branch?.pincode) && (
                                    <p className="text-sm ml-6">
                                      {[
                                        branch?.state,
                                        branch?.district,
                                        branch?.subDistrictOrCity,
                                        branch?.pincode
                                      ].filter(Boolean).join(", ")}
                                    </p>
                                  )
                                }
                              </>
                            )
                          }
                        </div>
                        {doctor?.fee != null && Number(doctor.fee) !== 0 && (
                          <p className="text-sm font-bold">
                            Fee : ₹{doctor.fee}
                          </p>
                        )}

                      </div>
                    </div>
                  ))}
                  {branchDetails.length > MAX_VISIBLE_CLINICS &&
                    !showAllClinics && (
                      <Button
                        variant="link"
                        className="!mt-0 p-0 text-xs font-bold text-primary underline"
                        onClick={() => setShowAllClinics(true)}
                      >
                        See more clinics
                      </Button>
                    )}
                </div>
              </div>

              <div className="space-y-6">
                {doctor?.aboutDoctor && doctor.aboutDoctor.trim() !== "" && (
                  <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                    <h2 className="mb-2 text-lg font-bold">About Doctor</h2>
                    <span>
                      {doctor?.aboutDoctor == null || doctor.aboutDoctor.trim() === "" ? (
                        <li className="text-sm">
                          NA
                        </li>
                      ) : (
                        doctor.aboutDoctor.split('\n').map((line, index) => (
                          <span key={index}>
                            {line}
                            <br />
                          </span>
                        ))
                      )}
                    </span>
                  </div>
                )}

                {/* <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                  <h2 className="mb-2 font-bold">Services</h2>
                  <ul className="list-disc space-y-2 pl-4 text-gray-600">
                    {doctor?.services?.length ? (
                      doctor.services.map((service) => (
                        <li key={service} className="text-sm">
                          {service}
                        </li>
                      ))
                    ) : (
                      <li className="text-sm">No services listed</li>
                    )}
                  </ul>
                </div> */}

                <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                  <h2 className="mb-2 font-bold">Specialisations</h2>
                  <ul className="list-disc space-y-2 pl-4 text-gray-600">
                    {doctor?.specialities?.length ? (
                      doctor.specialities.map((speciality) => (
                        <li key={speciality} className="text-sm">
                          {speciality}
                        </li>
                      ))
                    ) : (
                      <li className="text-sm">No specialities listed</li>
                    )}
                  </ul>
                </div>
                {Number(doctor?.yearOfExperience) > 0 && (
                  <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                    <h2 className="mb-2 font-bold">Experience</h2>
                    <ul className="list-disc space-y-2 pl-4 text-gray-600">
                      <li key={doctor?.yearOfExperience} className="text-sm">
                        {doctor?.yearOfExperience} years
                      </li>
                    </ul>
                  </div>)}



                {doctor && Array.isArray(doctor?.nameOfDegreeOrDiplomaObtained) && doctor.nameOfDegreeOrDiplomaObtained.length > 0 && (
                  <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                    <h2 className="mb-2 font-bold">Education</h2>
                    <ul className="list-disc space-y-2 pl-4 text-gray-600">
                      {doctor?.nameOfDegreeOrDiplomaObtained && doctor.nameOfDegreeOrDiplomaObtained.length > 0 ? (
                        doctor.nameOfDegreeOrDiplomaObtained.map((degree) => (
                          <li key={degree} className="text-sm">
                            {degree}
                          </li>
                        ))
                      ) : (
                        <li className="text-sm">NA</li>
                      )}
                    </ul>
                  </div>
                )}
                {doctor && Array.isArray(doctor?.awardAndRecognition) && doctor.awardAndRecognition.length > 0 && (
                  <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                    <h2 className="mb-2 font-bold">Rewards and Recognition</h2>
                    <ul className="list-disc space-y-2 pl-4 text-gray-600">
                      {doctor?.awardAndRecognition?.length ? (
                        doctor.awardAndRecognition.map((awd) => (
                          <li key={awd} className="text-sm">
                            {awd}
                          </li>
                        ))
                      ) : (
                        <li className="text-sm">
                          No rewards and recognition listed
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div
              className="lg:flex-1 lg:bg-white lg:w-2/5 lg:shadow-xl flex flex-col items-center"
              style={{
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <div
                className="lg:overflow-y-auto w-full max-w-lg flex flex-col h-[100vh] p-0"
                style={{
                  overflowY: "scroll",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <style>
                  {`
                    /* Hide scrollbar for Chrome, Safari, and Opera */
                    .lg\\:overflow-y-auto::-webkit-scrollbar {
                      display: none;
                    }

                    /* Hide scrollbar for Edge and Internet Explorer */
                    .lg\\:overflow-y-auto {
                      -ms-overflow-style: none;
                      scrollbar-width: none;
                    }
                  `}
                </style>
                <div className="lg:font-bold lg:text-2xl text-center mt-20 pb-2 mb-4 border-b-4 border-gray-200">
                  Book appointment
                </div>

                {step === 1 && (
                  <div className="flex flex-col items-center lg:mb-44">
                    <h2 className="font-bold text-center pb-10">
                      Select a date and time
                    </h2>
                    <Tabs
                      value={selectedDate}
                      onValueChange={(val) => {
                        setSelectedDate(val);
                        setData({
                          ...data,
                          branchDetails: data?.branchDetails ?? [],
                          slotTiming: "",
                          dayOfWeek: "",
                          slotDate: new Date(),
                        });
                      }}
                    >
                      <TabsList className="mb-4 h-[unset] w-full justify-center overflow-x-auto rounded-none border-b-2 p-0">
                        {Object.keys(slots).map((slot) => {
                          const today = format(new Date(), "dd-MM-yyyy");
                          const [day, month, year] = slot.split("-").map(Number);
                          const jsDate = new Date(year, month - 1, day);
                          const isToday = slot === today;
                          const dateStr = isToday
                            ? "Today"
                            : format(jsDate, "dd MMM");

                          return (
                            <TabsTrigger
                              key={slot}
                              value={slot}
                              className="rounded-t-md !border-0 text-sm font-bold text-foreground data-[state=active]:bg-primary-2 data-[state=active]:text-primary-foreground"
                            >
                              {dateStr}
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>

                      <TabsContent
                        value={selectedDate}
                        className="flex flex-col items-center"
                      >
                        {selectedDateSlots.length ? (
                          <>
                            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3">
                              {paginatedSlots.map((slot) => {
                                const isSelected =
                                  data?.slotTiming ===
                                  `${slot.from} - ${slot.to}`;
                                const now = new Date();
                                const [day, month, year] = selectedDate
                                  .split("-")
                                  .map(Number);
                                const [time, period] = slot.from.split(" ");
                                const [startHour, startMinute] = time
                                  .split(":")
                                  .map(Number);
                                const hour24 =
                                  period.toLowerCase() === "pm" &&
                                    startHour !== 12
                                    ? startHour + 12
                                    : period.toLowerCase() === "am" &&
                                      startHour === 12
                                      ? 0
                                      : startHour;
                                const slotStartTime = new Date(
                                  year,
                                  month - 1,
                                  day,
                                  hour24,
                                  startMinute,
                                );
                                const isSlotPassed = slotStartTime < now;
                                return (
                                  <Button
                                    key={slot.from}
                                    variant="outline"
                                    size="sm"
                                    disabled={isSlotPassed}
                                    className={cn(
                                      "rounded-full border-2 text-xs lowercase",
                                      {
                                        "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground":
                                          isSelected,
                                        "opacity-50 cursor-not-allowed":
                                          isSlotPassed,
                                      },
                                    )}
                                    onClick={() =>
                                      !isSlotPassed && handleSelectSlot(slot)
                                    }
                                  >
                                    {slot.from} - {slot.to}
                                  </Button>
                                );
                              })}
                            </div>
                            {selectedDateSlots.length > MAX_SLOTS &&
                              paginatedSlots.length <
                              selectedDateSlots.length && (
                                <Button
                                  className="mt-7 text-xs font-bold text-primary underline"
                                  variant="link"
                                  onClick={() =>
                                    setPaginatedSlots(selectedDateSlots)
                                  }
                                >
                                  See all slots
                                </Button>
                              )}
                          </>
                        ) : (
                          <p className="pb-28">
                            No slots available for this date
                          </p>
                        )}
                      </TabsContent>
                    </Tabs>
                    <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-end">
                      <Button onClick={handleNextStep}>Next</Button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <>
                    {mobile && !showModal && !showSignupModal ? (
                      <div className="flex flex-col items-center lg:mb-52">
                        <div className="mb-8 w-full">
                          <div className="mb-4 flex items-center gap-2">
                            <h2 className="text-left font-bold">
                              Select patient
                            </h2>
                          </div>
                          {isLoading ? (
                            <div className="flex justify-start mt-4">
                              <p>Loading patients...</p>
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="flex flex-wrap gap-4 justify-start max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                                {patients?.slice(0, 10).map((patient) => {
                                  if (!patient.name) return null;
                                  const isSelected =
                                    data?.patient?.id === patient.patientId;
                                  return (
                                    <Button
                                      key={patient.patientId}
                                      variant="outline"
                                      className={cn("rounded-full border-2", {
                                        "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground":
                                          isSelected,
                                      })}
                                      onClick={() =>
                                        handleSelectPatient(patient)
                                      }
                                    >
                                      {patient.name}
                                    </Button>
                                  );
                                })}
                                <button
                                  onClick={() => {
                                    if (!mobile) {
                                      setShowSignupModal(true);
                                    } else {
                                      setShowModal(true);
                                    }
                                  }}
                                  className="px-4 py-2 rounded-full border border-gray-300 bg-gray-100 hover:bg-gray-200 text-sm font-bold flex items-center justify-center cursor-pointer relative z-10"
                                >
                                  + Add New
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                          <Button onClick={handlePrevStep}>Back</Button>
                          <Button onClick={handleNextStep}>Next</Button>
                        </div>
                      </div>
                    ) : !mobile && !showVerifyPatient && !showPatientSelection ? (
                      <div
                        className="flex flex-col items-center lg:pb-32"
                        style={{
                          overflowY: "auto",
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        <h2 className="my-4 text-left font-bold">
                          Add Symptoms
                        </h2>
                        <Textarea
                          placeholder="Type your symptoms here or select from the list below"
                          rows={5}
                          value={data?.symptoms?.join(", ")}
                          onChange={(e) => handleTypeSymptoms(e.target.value)}
                        />
                        {isSymptomsLoading ? (
                          <p>Loading...</p>
                        ) : (
                          !!symptoms.length && (
                            <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-3">
                              {symptoms.map((symptom) => {
                                const isSelected =
                                  data?.symptoms?.includes(symptom);
                                return (
                                  <li
                                    key={symptom}
                                    className={cn(
                                      "cursor-pointer rounded-full border px-4 py-2 text-xs",
                                      {
                                        "border-primary-2 bg-primary-2 text-primary-foreground":
                                          isSelected,
                                      },
                                    )}
                                    onClick={() =>
                                      handleToggleSelectSymptom(symptom)
                                    }
                                  >
                                    {symptom}
                                  </li>
                                );
                              })}
                            </ul>
                          )
                        )}
                        <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                          <Button onClick={handlePrevStep}>Back</Button>
                          <Button onClick={handleNextStep}>Next</Button>
                        </div>
                      </div>
                    ) : !mobile && showVerifyPatient ? (
                      <div className="mt-6 w-full">
                        <PopverifyPatient
                          onClose={() => {
                            setShowVerifyPatient(false);
                            setShowPatientSelection(false);
                            setStep(2);
                            navigate(
                              `/doctors/${professionalId}?step=2&backUrl=${encodeURIComponent(backUrl)}`
                            );
                          }}
                        />
                      </div>
                    ) : !mobile && showPatientSelection ? (
                      <div className="flex flex-col items-center lg:mb-52">
                        <div className="mb-8 w-full">
                          <div className="mb-4 flex items-center gap-2">
                            <h2 className="text-left font-bold">
                              Select patient
                            </h2>
                          </div>
                          {isLoading ? (
                            <div className="flex justify-start mt-4">
                              <p>Loading patients...</p>
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="flex flex-wrap gap-4 justify-start max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                                {patients?.slice(0, 10).map((patient) => {
                                  if (!patient.name) return null;
                                  const isSelected =
                                    data?.patient?.id === patient.patientId;
                                  return (
                                    <Button
                                      key={patient.patientId}
                                      variant="outline"
                                      className={cn("rounded-full border-2", {
                                        "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground":
                                          isSelected,
                                      })}
                                      onClick={() =>
                                        handleSelectPatient(patient)
                                      }
                                    >
                                      {patient.name}
                                    </Button>
                                  );
                                })}
                                <button
                                  onClick={() => setShowModal(true)}
                                  className="px-4 py-2 rounded-full border border-gray-300 bg-gray-100 hover:bg-gray-200 text-sm font-bold flex items-center justify-center cursor-pointer relative z-10"
                                >
                                  + Add New
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                          <Button onClick={handlePrevStep}>Back</Button>
                          <Button onClick={handleNextStep}>Next</Button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}

                {step === 2 && showModal && (
                  <div className="w-full">
                    <PopaddPatient
                      onClose={(newPatient?: { patientId: string; name: string }) => {
                        setShowModal(false);
                        if (newPatient?.patientId && newPatient.name) {
                          handleSelectPatient({
                            patientId: newPatient.patientId,
                            name: newPatient.name,
                          } as unknown as IPatient); // Safer cast
                        }
                      }}
                    />
                  </div>
                )}
                {step === 3 && mobile && (
                  <div
                    className="flex flex-col items-center lg:pb-32"
                    style={{
                      overflowY: "auto",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    <h2 className="my-4 text-left font-bold">Add Symptoms</h2>
                    <Textarea
                      placeholder="Type your symptoms here or select from the list below"
                      rows={5}
                      value={data?.symptoms?.join(", ")}
                      onChange={(e) => handleTypeSymptoms(e.target.value)}
                    />
                    {isSymptomsLoading ? (
                      <p>Loading...</p>
                    ) : (
                      !!symptoms.length && (
                        <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-3">
                          {symptoms.map((symptom) => {
                            const isSelected = data?.symptoms?.includes(symptom);
                            return (
                              <li
                                key={symptom}
                                className={cn(
                                  "cursor-pointer rounded-full border px-4 py-2 text-xs",
                                  {
                                    "border-primary-2 bg-primary-2 text-primary-foreground":
                                      isSelected,
                                  },
                                )}
                                onClick={() => handleToggleSelectSymptom(symptom)}
                              >
                                {symptom}
                              </li>
                            );
                          })}
                        </ul>
                      )
                    )}
                    <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                      <Button onClick={handlePrevStep}>Back</Button>
                      <Button onClick={handleContinue}>Next</Button>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="flex flex-col items-center w-full lg:mb-44">
                    {showAddHealthRecords ? (
                      <PopAddHealthRecords
                        onClose={() => setShowAddHealthRecords(false)}
                      />
                    ) : (
                      <LargeConfirmAppointment
                        onAddHealthRecords={() =>
                          setShowAddHealthRecords(true)
                        }
                      />
                    )}
                    <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                      <Button onClick={handlePrevStep}>Back</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DoctorDetails1;